// Define the type for Target Audience
import dayjs from 'dayjs';
import type { ComponentProps } from 'react';

import type { EventPanel, EventPanelStatus } from '../../../components/global/EventPanel/EventPanel.tsx';
import { env } from '../../../env.ts';

interface TargetAudience {
  target_audiences_id?: {
    name?: string | null;
  } | null;
}

interface Event {
  id?: number | string | null;
  image?: string | null;
  category?: {
    title?: string | null;
  } | null;
  target_audiences?: TargetAudience[] | null;
  start?: string | null;
  name?: string | null;
  venue?: {
    commercial_name?: string | null;
  } | null;
  type?: string | null;
  custom_venue?: string | null;
  events_group?: number | string | Record<string, unknown> | null;
  decline_reason?: string | null;
}

const parseType = (type?: string | null): 'online' | 'live' | 'secret' | undefined => {
  if (type === 'online') {
    return 'online' as const;
  }

  if (type === 'live') {
    return 'live' as const;
  }

  if (type === 'secret') {
    return 'secret' as const;
  }

  return undefined;
};

const getLocationName = (event: Event) => {
  if (event.venue?.commercial_name) {
    return event.venue.commercial_name;
  }

  if (event.custom_venue && event.custom_venue !== '') {
    return `${event.custom_venue} (custom)`;
  }
};

export const parseEvents = (
  events: Event[] | undefined,
  status: EventPanelStatus,
):
  | (Omit<ComponentProps<typeof EventPanel>, 'onDropdownClick'> & {
      id: string;
      eventsGroup: number | null;
    })[]
  | undefined => {
  return events?.map((event) => {
    const targetAudienceNames =
      event.target_audiences
        ?.map((targetAudience) => targetAudience.target_audiences_id?.name)
        .filter((name): name is string => !!name) ?? []; // Ensure only non-empty strings are kept

    return {
      id: event.id?.toString(10) ?? '',
      imageSrc: event.image ? `${env.VITE_DIRECTUS_API_URL}/assets/${event.image}` : null,
      tags: [event.category?.title, ...targetAudienceNames].filter((tag): tag is string => !!tag),
      date: event.start ? dayjs(event.start).toDate() : new Date(),
      status,
      type: parseType(event.type),
      title: event.name ?? undefined,
      locationName: getLocationName(event),
      eventsGroup: typeof event.events_group === 'number' ? event.events_group : null,
      declineReason: event.decline_reason ?? undefined,
    };
  });
};
